import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "../components/Seo";
import {
  HandsPraying,
  Heart,
  EnvelopeSimple,
  Newspaper,
  CurrencyDollar,
  MagnifyingGlass,
  SignOut,
  Calendar,
  X,
  ChartLineUp,
  Cross,
  FloppyDisk,
  DownloadSimple,
  Trash,
  ArrowsDownUp,
  Tray as Inbox,
  Gear,
  Phone as PhoneIcon,
  User as UserIcon,
  ArrowLeft,
  ChatCircleDots,
  ShoppingBag,
  HeartStraight,
} from "@phosphor-icons/react";
import { API_BASE } from "../lib/site";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import NewsletterManager from "../components/NewsletterManager";
import GuideAnalytics from "../components/GuideAnalytics";
import ClassRegistrationsAdmin from "../components/ClassRegistrationsAdmin";
import OrganizationBookingsAdmin from "../components/OrganizationBookingsAdmin";
import BulkOrdersAdmin from "../components/BulkOrdersAdmin";
import SponsorIntentsAdmin from "../components/SponsorIntentsAdmin";
import ConfirmDialog from "../components/ConfirmDialog";

// Composite key for the Inbox multi-select set. Inbox mixes 4 collections,
// so `_type:id` uniquely identifies a message even if two collections ever
// share the same UUID.
const inboxKey = (item) => `${item._type}:${item.id}`;

// ---------- Configuration ----------
const TYPE_CONFIG = {
  prayer: {
    label: "Prayer",
    icon: HandsPraying,
    collection: "prayer-requests",
    endpoint: "/admin/prayer-requests",
    bodyField: "request",
    accent: "bg-primary/10 text-primary",
  },
  support: {
    label: "Support",
    icon: Heart,
    collection: "support-requests",
    endpoint: "/admin/support-requests",
    bodyField: "message",
    accent: "bg-rose-100 text-rose-700",
  },
  contact: {
    label: "Contact",
    icon: EnvelopeSimple,
    collection: "contact-messages",
    endpoint: "/admin/contact-messages",
    bodyField: "message",
    accent: "bg-amber-100 text-amber-800",
  },
  newsletter: {
    label: "Newsletter",
    icon: Newspaper,
    collection: "newsletter-subscribers",
    endpoint: "/admin/newsletter-subscribers",
    bodyField: null, // subscribers don't have a message body
    accent: "bg-emerald-100 text-emerald-800",
  },
};

const STATUS_OPTIONS = [
  { key: "new", label: "New", pill: "bg-primary/10 text-primary" },
  { key: "in_progress", label: "In Progress", pill: "bg-amber-100 text-amber-800" },
  { key: "completed", label: "Completed", pill: "bg-emerald-100 text-emerald-700" },
];

const SUPPORT_REQUEST_TYPES = ["all", "prayer", "emotional", "grief", "speaking", "workshop"];

// ---------- Helpers ----------
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fmtDateShort = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const inferStatus = (item) => {
  if (item.status && ["new", "in_progress", "completed"].includes(item.status)) {
    return item.status;
  }
  // Newsletter has no status by design → treat as "new" (informational only)
  if (item._type === "newsletter") return "new";
  return item.handled ? "completed" : "new";
};

const getBody = (item) => {
  const cfg = TYPE_CONFIG[item._type];
  if (!cfg?.bodyField) return "";
  return item[cfg.bodyField] || "";
};

const buildCSV = (rows, headers) => {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[,\n"]/.test(s) ? `"${s}"` : s;
  };
  const head = headers.map((h) => escape(h.label)).join(",");
  const body = rows
    .map((r) => headers.map((h) => escape(typeof h.value === "function" ? h.value(r) : r[h.value])).join(","))
    .join("\n");
  return `${head}\n${body}`;
};

const triggerDownload = (filename, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

const CSV_HEADERS = {
  prayer: [
    { label: "Name", value: "name" },
    { label: "Email", value: "email" },
    { label: "Phone", value: "phone" },
    { label: "Anonymous", value: (r) => (r.is_anonymous ? "Yes" : "No") },
    { label: "Allow Share", value: (r) => (r.allow_share ? "Yes" : "No") },
    { label: "Request", value: "request" },
    { label: "Status", value: (r) => inferStatus(r) },
    { label: "Internal Notes", value: "internal_notes" },
    { label: "Source Page", value: "source_page" },
    { label: "Created At", value: "created_at" },
  ],
  support: [
    { label: "Name", value: "name" },
    { label: "Email", value: "email" },
    { label: "Phone", value: "phone" },
    { label: "Request Type", value: "request_type" },
    { label: "Subject", value: "subject" },
    { label: "Message", value: "message" },
    { label: "Status", value: (r) => inferStatus(r) },
    { label: "Internal Notes", value: "internal_notes" },
    { label: "Source Page", value: "source_page" },
    { label: "Created At", value: "created_at" },
  ],
  contact: [
    { label: "Name", value: "name" },
    { label: "Email", value: "email" },
    { label: "Phone", value: "phone" },
    { label: "Inquiry Type", value: "inquiry_type" },
    { label: "Subject", value: "subject" },
    { label: "Message", value: "message" },
    { label: "Status", value: (r) => inferStatus(r) },
    { label: "Internal Notes", value: "internal_notes" },
    { label: "Source Page", value: "source_page" },
    { label: "Created At", value: "created_at" },
  ],
  newsletter: [
    { label: "Email", value: "email" },
    { label: "Name", value: "name" },
    { label: "Source Page", value: "source_page" },
    { label: "Subscribed At", value: "created_at" },
  ],
};

// ---------- Small UI Bits ----------
const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div
    className="bg-white rounded-2xl border border-ink-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-5"
    data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
  >
    <div className="flex items-start justify-between">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-softblue text-primary">
        <Icon size={18} weight="duotone" />
      </span>
      <span className="text-[10px] uppercase tracking-[0.15em] text-ink-500 font-semibold">{label}</span>
    </div>
    <div className="mt-3 font-heading text-2xl font-medium text-ink-900">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-ink-500">{sub}</div>}
  </div>
);

const StatusPill = ({ status }) => {
  const opt = STATUS_OPTIONS.find((o) => o.key === status) || STATUS_OPTIONS[0];
  return (
    <span
      className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full font-semibold ${opt.pill}`}
      data-testid={`status-pill-${status}`}
    >
      {opt.label}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const cfg = TYPE_CONFIG[type];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full font-semibold ${cfg.accent}`}
    >
      <Icon size={11} weight="duotone" />
      {cfg.label}
    </span>
  );
};

// ---------- Detail Panel ----------
const DetailPanel = ({ item, onStatusChange, onNotesSave, onDelete, onClose }) => {
  const [notes, setNotes] = useState(item?.internal_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setNotes(item?.internal_notes || "");
    setConfirmDelete(false);
  }, [item?.id]);

  if (!item) {
    return (
      <div
        className="hidden lg:flex flex-col items-center justify-center bg-white rounded-2xl border border-ink-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-10 text-center h-full min-h-[400px]"
        data-testid="admin-detail-empty"
      >
        <Inbox size={48} weight="duotone" className="text-ink-300 mb-4" />
        <p className="text-ink-500 text-sm">Select a submission to view details</p>
      </div>
    );
  }

  const status = inferStatus(item);
  const body = getBody(item);

  const handleStatus = async (newStatus) => {
    setChangingStatus(true);
    try {
      await onStatusChange(item, newStatus);
    } finally {
      setChangingStatus(false);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await onNotesSave(item, notes);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDelete = async () => {
    await onDelete(item);
    setConfirmDelete(false);
  };

  return (
    <div
      className="bg-white rounded-2xl border border-ink-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6 sm:p-7 h-full overflow-y-auto"
      data-testid={`admin-detail-${item.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 pb-5 border-b border-ink-100">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <TypeBadge type={item._type} />
            {item._type !== "newsletter" && <StatusPill status={status} />}
            {item.request_type && (
              <span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-primary/5 text-primary font-semibold">
                {item.request_type}
              </span>
            )}
            {item.inquiry_type && (
              <span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-primary/5 text-primary font-semibold">
                {item.inquiry_type}
              </span>
            )}
          </div>
          <h2 className="font-heading text-2xl text-ink-900 font-medium truncate">
            {item.name || item.email || "Unnamed"}
          </h2>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-500">
            {item.email && (
              <a href={`mailto:${item.email}`} className="hover:text-primary" data-testid="detail-email-link">
                {item.email}
              </a>
            )}
            {item.phone && (
              <span className="inline-flex items-center gap-1">
                <PhoneIcon size={12} weight="duotone" />
                {item.phone}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Calendar size={12} weight="duotone" />
              {fmtDate(item.created_at)}
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="detail-close-btn"
          className="lg:hidden shrink-0 w-9 h-9 rounded-full border border-ink-200 flex items-center justify-center text-ink-600 hover:border-primary hover:text-primary"
        >
          <ArrowLeft size={16} weight="bold" />
        </button>
      </div>

      {/* Body */}
      {item.subject && (
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-[0.15em] text-primary font-semibold">Subject</div>
          <div className="mt-1 text-ink-800 font-medium">{item.subject}</div>
        </div>
      )}

      {body && (
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-[0.15em] text-primary font-semibold">
            {item._type === "prayer" ? "Prayer request" : "Message"}
          </div>
          <div className="mt-2 whitespace-pre-wrap text-ink-800 leading-relaxed bg-softblue/20 rounded-xl px-4 py-3 border border-ink-100">
            {body}
          </div>
        </div>
      )}

      {item._type === "prayer" && (
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-ink-600">
          <span>
            <span className="text-ink-500">Anonymous: </span>
            <span className="font-semibold">{item.is_anonymous ? "Yes" : "No"}</span>
          </span>
          <span>
            <span className="text-ink-500">Allow share: </span>
            <span className="font-semibold">{item.allow_share ? "Yes" : "No"}</span>
          </span>
        </div>
      )}

      {item.source_page && (
        <div className="mt-4 text-xs text-ink-500">
          Submitted from: <span className="text-ink-700 font-mono">{item.source_page}</span>
        </div>
      )}

      {/* Status control */}
      {item._type !== "newsletter" && (
        <div className="mt-6 pt-5 border-t border-ink-100">
          <div className="text-[11px] uppercase tracking-[0.15em] text-primary font-semibold mb-2">
            Status
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => handleStatus(opt.key)}
                disabled={changingStatus || status === opt.key}
                data-testid={`detail-status-${opt.key}`}
                className={`text-xs font-semibold px-4 py-2 rounded-full transition-all disabled:cursor-not-allowed ${
                  status === opt.key
                    ? `${opt.pill} ring-2 ring-offset-1 ring-primary/30`
                    : "bg-white border border-ink-200 text-ink-700 hover:border-primary/50 hover:text-primary"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Internal notes */}
      {item._type !== "newsletter" && (
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-[0.15em] text-primary font-semibold mb-2">
            Internal follow-up notes
          </div>
          <textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Private notes for the ministry team…"
            data-testid="detail-notes-textarea"
            className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-y text-sm"
          />
          <button
            onClick={handleSaveNotes}
            disabled={savingNotes || notes === (item.internal_notes || "")}
            data-testid="detail-notes-save"
            className="mt-3 inline-flex items-center gap-1.5 bg-primary text-white text-sm font-semibold rounded-full px-5 py-2 hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FloppyDisk size={14} weight="duotone" />
            {savingNotes ? "Saving…" : "Save notes"}
          </button>
        </div>
      )}

      {/* Delete */}
      <div className="mt-8 pt-5 border-t border-ink-100">
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            data-testid="detail-delete-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-700 hover:text-rose-800"
          >
            <Trash size={14} weight="duotone" />
            Delete submission
          </button>
        ) : (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
            <p className="text-sm text-rose-800 font-semibold">
              Delete this submission permanently?
            </p>
            <p className="text-xs text-rose-700 mt-1">This cannot be undone.</p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleDelete}
                data-testid="detail-delete-confirm"
                className="text-xs font-semibold px-4 py-2 rounded-full bg-rose-600 text-white hover:bg-rose-700"
              >
                Yes, delete
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                data-testid="detail-delete-cancel"
                className="text-xs font-semibold px-4 py-2 rounded-full bg-white border border-ink-200 text-ink-700 hover:border-ink-400"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Main Admin ----------
const Admin = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // View mode: "inbox" | "chapel"
  const [mode, setMode] = useState("inbox");

  // Data
  const [stats, setStats] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // "all" | prayer | support | contact | newsletter
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | new | in_progress | completed
  const [supportKindFilter, setSupportKindFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc"); // desc = newest first, asc = oldest first

  // Selection
  const [selectedId, setSelectedId] = useState(null);

  // Inbox multi-select — Set of composite keys `${_type}:${id}`.
  const [selectedInboxKeys, setSelectedInboxKeys] = useState(() => new Set());
  const [inboxConfirmDelete, setInboxConfirmDelete] = useState(null); // {keys:string[], items:Item[]}
  const [inboxBulkDeleting, setInboxBulkDeleting] = useState(false);

  // Ensure /admin is not indexed by mutating the existing robots meta tag
  // (index.html ships a default 'index, follow' for the public site).
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');
    const previous = existing?.getAttribute("content");
    if (existing) {
      existing.setAttribute("content", "noindex,nofollow");
    }
    return () => {
      if (existing) {
        existing.setAttribute("content", previous || "index, follow");
      }
    };
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/stats`);
      setStats(res.data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session ended. Please sign in again.");
        navigate("/login");
      }
    }
  }, [navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const types = Object.keys(TYPE_CONFIG);
      const results = await Promise.all(
        types.map((t) =>
          axios
            .get(`${API_BASE}${TYPE_CONFIG[t].endpoint}`)
            .then((r) => (r.data || []).map((d) => ({ ...d, _type: t })))
            .catch(() => [])
        )
      );
      const merged = results.flat();
      setAllItems(merged);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session ended. Please sign in again.");
        navigate("/login");
        return;
      }
      toast.error("Couldn't load submissions");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchStats();
    fetchAll();
  }, [fetchStats, fetchAll]);

  // Filtered + sorted list (client-side)
  const filteredItems = useMemo(() => {
    let list = allItems;

    if (typeFilter !== "all") {
      list = list.filter((it) => it._type === typeFilter);
    }

    if (statusFilter !== "all") {
      list = list.filter((it) => inferStatus(it) === statusFilter);
    }

    if (typeFilter === "support" && supportKindFilter !== "all") {
      list = list.filter((it) => it.request_type === supportKindFilter);
    }

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((it) => {
        const hay = [
          it.name,
          it.email,
          it.phone,
          it.subject,
          it.inquiry_type,
          it.request_type,
          getBody(it),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    list = [...list].sort((a, b) => {
      const ad = new Date(a.created_at || 0).getTime();
      const bd = new Date(b.created_at || 0).getTime();
      return sortOrder === "desc" ? bd - ad : ad - bd;
    });

    return list;
  }, [allItems, typeFilter, statusFilter, supportKindFilter, query, sortOrder]);

  // Selected item lookup
  const selected = useMemo(
    () => filteredItems.find((i) => i.id === selectedId) || null,
    [filteredItems, selectedId]
  );

  // Actions
  const handleStatusChange = async (item, newStatus) => {
    const cfg = TYPE_CONFIG[item._type];
    try {
      const res = await axios.patch(`${API_BASE}/admin/${cfg.collection}/${item.id}`, {
        status: newStatus,
      });
      const updated = { ...res.data, _type: item._type };
      setAllItems((prev) => prev.map((it) => (it.id === item.id && it._type === item._type ? updated : it)));
      fetchStats();
      toast.success("Status updated");
    } catch {
      toast.error("Couldn't update status");
    }
  };

  const handleNotesSave = async (item, internal_notes) => {
    const cfg = TYPE_CONFIG[item._type];
    try {
      const res = await axios.patch(`${API_BASE}/admin/${cfg.collection}/${item.id}`, {
        internal_notes,
      });
      const updated = { ...res.data, _type: item._type };
      setAllItems((prev) => prev.map((it) => (it.id === item.id && it._type === item._type ? updated : it)));
      toast.success("Notes saved");
    } catch {
      toast.error("Couldn't save notes");
    }
  };

  const handleDelete = async (item) => {
    const cfg = TYPE_CONFIG[item._type];
    try {
      await axios.delete(`${API_BASE}/admin/${cfg.collection}/${item.id}`);
      const key = inboxKey(item);
      setAllItems((prev) => prev.filter((it) => !(it.id === item.id && it._type === item._type)));
      setSelectedInboxKeys((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      if (selectedId === item.id) setSelectedId(null);
      fetchStats();
      toast.success("Message deleted");
    } catch {
      toast.error("Couldn't delete");
    }
  };

  // ---- Inbox multi-select helpers ----
  const visibleKeys = useMemo(
    () => new Set(filteredItems.map(inboxKey)),
    [filteredItems]
  );

  // Prune selections whenever the visible list changes so the "N selected" bar
  // NEVER silently includes rows the admin can't currently see.
  useEffect(() => {
    setSelectedInboxKeys((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set();
      prev.forEach((k) => {
        if (visibleKeys.has(k)) next.add(k);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [visibleKeys]);

  const toggleInboxRow = useCallback((item) => {
    const k = inboxKey(item);
    setSelectedInboxKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }, []);

  const clearInboxSelection = useCallback(
    () => setSelectedInboxKeys(new Set()),
    []
  );

  const askBulkDelete = () => {
    if (selectedInboxKeys.size === 0) return;
    const keys = Array.from(selectedInboxKeys);
    const set = new Set(keys);
    const items = filteredItems.filter((i) => set.has(inboxKey(i)));
    setInboxConfirmDelete({ keys, items });
  };

  const doBulkDelete = async () => {
    if (!inboxConfirmDelete) return;
    const { items } = inboxConfirmDelete;
    const payload = {
      items: items.map((i) => ({
        collection: TYPE_CONFIG[i._type].collection,
        id: i.id,
      })),
    };
    setInboxBulkDeleting(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/inbox/bulk-delete`, payload);
      const removed = new Set(items.map(inboxKey));
      setAllItems((prev) => prev.filter((it) => !removed.has(inboxKey(it))));
      // Also clear the current detail selection if it was in the removed set.
      if (
        selectedId &&
        items.some((i) => i.id === selectedId)
      ) {
        setSelectedId(null);
      }
      clearInboxSelection();
      fetchStats();
      const n = res.data?.deleted ?? items.length;
      const requested = res.data?.requested ?? items.length;
      if (n < requested) {
        toast.success(
          `${n} of ${requested} message${requested === 1 ? "" : "s"} deleted (${
            requested - n
          } no longer existed)`
        );
      } else {
        toast.success(`${n} message${n === 1 ? "" : "s"} deleted`);
      }
      setInboxConfirmDelete(null);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session ended. Please sign in again.");
      } else {
        toast.error("Could not delete. Please try again.");
      }
    } finally {
      setInboxBulkDeleting(false);
    }
  };

  const handleExportCSV = () => {
    // Export whichever collection is currently in view.
    // If "all" types, export each type into a separate CSV; otherwise just the active type.
    const targetType = typeFilter === "all" ? null : typeFilter;
    if (targetType) {
      const rows = filteredItems.filter((i) => i._type === targetType);
      if (rows.length === 0) {
        toast.error("Nothing to export");
        return;
      }
      const csv = buildCSV(rows, CSV_HEADERS[targetType]);
      const stamp = new Date().toISOString().slice(0, 10);
      triggerDownload(`bal-${TYPE_CONFIG[targetType].collection}-${stamp}.csv`, csv);
      toast.success(`Exported ${rows.length} ${TYPE_CONFIG[targetType].label} record${rows.length === 1 ? "" : "s"}`);
      return;
    }
    // "All" → one file per type from the currently filtered set
    let exported = 0;
    Object.keys(TYPE_CONFIG).forEach((t) => {
      const rows = filteredItems.filter((i) => i._type === t);
      if (rows.length === 0) return;
      const csv = buildCSV(rows, CSV_HEADERS[t]);
      const stamp = new Date().toISOString().slice(0, 10);
      triggerDownload(`bal-${TYPE_CONFIG[t].collection}-${stamp}.csv`, csv);
      exported += rows.length;
    });
    if (exported === 0) toast.error("Nothing to export");
    else toast.success(`Exported ${exported} record${exported === 1 ? "" : "s"} across all collections`);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const totalDonations = useMemo(
    () =>
      `$${(stats?.donations?.total_amount || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    [stats]
  );

  return (
    <div className="bg-softblue/30 min-h-[calc(100vh-5rem)] py-8" data-testid="page-admin">
      <Seo title="Admin Dashboard" noindex />
      <div className="container-x">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              Ministry dashboard
            </span>
            <h1 className="mt-1 font-heading text-3xl font-medium text-ink-900">
              Welcome, {user?.name?.split(" ")?.[0] || "friend"}.
            </h1>
            <p className="mt-0.5 text-sm text-ink-500">{user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-white border border-ink-200 rounded-full p-1" data-testid="admin-mode-toggle">
              <button
                onClick={() => setMode("inbox")}
                data-testid="admin-mode-inbox"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                  mode === "inbox" ? "bg-primary text-white shadow-sm" : "text-ink-700 hover:text-primary"
                }`}
              >
                <Inbox size={14} weight="duotone" />
                Inbox
              </button>
              <button
                onClick={() => setMode("newsletter")}
                data-testid="admin-mode-newsletter"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                  mode === "newsletter" ? "bg-primary text-white shadow-sm" : "text-ink-700 hover:text-primary"
                }`}
              >
                <Newspaper size={14} weight="duotone" />
                Newsletter
              </button>
              <button
                onClick={() => setMode("chapel")}
                data-testid="admin-mode-chapel"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                  mode === "chapel" ? "bg-primary text-white shadow-sm" : "text-ink-700 hover:text-primary"
                }`}
              >
                <Gear size={14} weight="duotone" />
                Chapel
              </button>
              <button
                onClick={() => setMode("guide")}
                data-testid="admin-mode-guide"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                  mode === "guide" ? "bg-primary text-white shadow-sm" : "text-ink-700 hover:text-primary"
                }`}
              >
                <ChatCircleDots size={14} weight="duotone" />
                Guide
              </button>
              <button
                onClick={() => setMode("classes")}
                data-testid="admin-mode-classes"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                  mode === "classes" ? "bg-primary text-white shadow-sm" : "text-ink-700 hover:text-primary"
                }`}
              >
                <Calendar size={14} weight="duotone" />
                Online Classes
              </button>
              <button
                onClick={() => setMode("bookings")}
                data-testid="admin-mode-bookings"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                  mode === "bookings" ? "bg-primary text-white shadow-sm" : "text-ink-700 hover:text-primary"
                }`}
              >
                <Calendar size={14} weight="duotone" />
                Organization Bookings
              </button>
              <button
                onClick={() => setMode("bulk-orders")}
                data-testid="admin-mode-bulk-orders"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                  mode === "bulk-orders" ? "bg-primary text-white shadow-sm" : "text-ink-700 hover:text-primary"
                }`}
              >
                <ShoppingBag size={14} weight="duotone" />
                Bulk Orders
              </button>
              <button
                onClick={() => setMode("sponsor-intents")}
                data-testid="admin-mode-sponsor-intents"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                  mode === "sponsor-intents" ? "bg-primary text-white shadow-sm" : "text-ink-700 hover:text-primary"
                }`}
              >
                <HeartStraight size={14} weight="duotone" />
                Sponsor Intents
              </button>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 border border-ink-200 bg-white text-ink-800 rounded-full px-4 py-2 text-sm font-semibold hover:border-primary hover:text-primary transition-all"
            >
              <SignOut size={14} weight="duotone" />
              Sign out
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatCard
              icon={HandsPraying}
              label="Prayer"
              value={stats.prayer_requests.total}
              sub={`${stats.prayer_requests.pending} pending`}
            />
            <StatCard
              icon={Heart}
              label="Support"
              value={stats.support_requests.total}
              sub={`${stats.support_requests.pending} pending`}
            />
            <StatCard
              icon={EnvelopeSimple}
              label="Contact"
              value={stats.contact_messages.total}
              sub={`${stats.contact_messages.pending} pending`}
            />
            <StatCard icon={Newspaper} label="Subscribers" value={stats.newsletter_subscribers.total} />
            <StatCard
              icon={ShoppingBag}
              label="Bulk Orders"
              value={stats.bulk_orders?.total ?? 0}
              sub={`${stats.bulk_orders?.new ?? 0} new`}
            />
            <StatCard
              icon={ChartLineUp}
              label="Donations"
              value={totalDonations}
              sub={`${stats.donations.count} gifts`}
            />
          </div>
        )}

        {mode === "chapel" ? (
          <ChapelEditor />
        ) : mode === "newsletter" ? (
          <NewsletterManager />
        ) : mode === "guide" ? (
          <GuideAnalytics />
        ) : mode === "classes" ? (
          <ClassRegistrationsAdmin onDataChange={fetchStats} />
        ) : mode === "bookings" ? (
          <OrganizationBookingsAdmin onDataChange={fetchStats} />
        ) : mode === "bulk-orders" ? (
          <BulkOrdersAdmin onDataChange={fetchStats} />
        ) : mode === "sponsor-intents" ? (
          <SponsorIntentsAdmin onDataChange={fetchStats} />
        ) : (
          <InboxView
            loading={loading}
            items={filteredItems}
            allItems={allItems}
            selected={selected}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            query={query}
            setQuery={setQuery}
            typeFilter={typeFilter}
            setTypeFilter={(t) => {
              setTypeFilter(t);
              setSupportKindFilter("all");
              setSelectedId(null);
            }}
            statusFilter={statusFilter}
            setStatusFilter={(s) => {
              setStatusFilter(s);
              setSelectedId(null);
            }}
            supportKindFilter={supportKindFilter}
            setSupportKindFilter={setSupportKindFilter}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            onStatusChange={handleStatusChange}
            onNotesSave={handleNotesSave}
            onDelete={handleDelete}
            onExportCSV={handleExportCSV}
            selectedInboxKeys={selectedInboxKeys}
            onToggleRow={toggleInboxRow}
            onClearSelection={clearInboxSelection}
            onSelectAllVisible={() =>
              setSelectedInboxKeys(new Set(filteredItems.map(inboxKey)))
            }
            onAskBulkDelete={askBulkDelete}
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(inboxConfirmDelete)}
        title={
          inboxConfirmDelete
            ? `Delete ${inboxConfirmDelete.keys.length} selected message${
                inboxConfirmDelete.keys.length === 1 ? "" : "s"
              }?`
            : ""
        }
        description="This will permanently remove the selected messages. This action cannot be undone."
        confirmLabel="Delete Selected"
        busy={inboxBulkDeleting}
        onConfirm={doBulkDelete}
        onCancel={() => setInboxConfirmDelete(null)}
        testId="inbox-confirm-bulk-delete"
      />
    </div>
  );
};

// ---------- Inbox View ----------
const InboxView = ({
  loading,
  items,
  allItems,
  selected,
  selectedId,
  setSelectedId,
  query,
  setQuery,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  supportKindFilter,
  setSupportKindFilter,
  sortOrder,
  setSortOrder,
  onStatusChange,
  onNotesSave,
  onDelete,
  onExportCSV,
  selectedInboxKeys,
  onToggleRow,
  onClearSelection,
  onSelectAllVisible,
  onAskBulkDelete,
}) => {
  const typeCounts = useMemo(() => {
    const counts = { all: allItems.length };
    Object.keys(TYPE_CONFIG).forEach((t) => {
      counts[t] = allItems.filter((i) => i._type === t).length;
    });
    return counts;
  }, [allItems]);

  const allVisibleSelected =
    items.length > 0 && items.every((i) => selectedInboxKeys.has(inboxKey(i)));

  return (
    <div data-testid="admin-inbox">
      {/* Toolbar */}
      <div className="bg-white rounded-2xl border border-ink-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-4 mb-4 space-y-3">
        {/* Search + sort + export */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-softblue/40 rounded-full px-4 py-2.5">
            <MagnifyingGlass size={16} className="text-ink-500" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, subject, message…"
              data-testid="admin-search-input"
              className="flex-1 bg-transparent outline-none text-sm text-ink-800 placeholder:text-ink-500"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="text-ink-500 hover:text-ink-800"
                data-testid="admin-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button
            onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
            data-testid="admin-sort-toggle"
            className="inline-flex items-center gap-1.5 border border-ink-200 bg-white rounded-full px-4 py-2 text-sm font-semibold text-ink-700 hover:border-primary hover:text-primary transition-all"
          >
            <ArrowsDownUp size={14} weight="duotone" />
            {sortOrder === "desc" ? "Newest first" : "Oldest first"}
          </button>
          <button
            onClick={onExportCSV}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-1.5 bg-primary text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-primary-dark transition-all"
          >
            <DownloadSimple size={14} weight="duotone" />
            Export CSV
          </button>
        </div>

        {/* Type chips */}
        <div className="flex flex-wrap items-center gap-1.5" data-testid="admin-type-filter">
          <TypeChip
            active={typeFilter === "all"}
            onClick={() => setTypeFilter("all")}
            testId="admin-type-all"
            label="All"
            count={typeCounts.all}
          />
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
            <TypeChip
              key={key}
              active={typeFilter === key}
              onClick={() => setTypeFilter(key)}
              testId={`admin-type-${key}`}
              icon={cfg.icon}
              label={cfg.label}
              count={typeCounts[key]}
            />
          ))}
        </div>

        {/* Status + support-kind row */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 bg-softblue/40 rounded-full p-1" data-testid="admin-status-filter">
            <StatusChip
              active={statusFilter === "all"}
              onClick={() => setStatusFilter("all")}
              testId="admin-status-all"
              label="All"
            />
            {STATUS_OPTIONS.map((o) => (
              <StatusChip
                key={o.key}
                active={statusFilter === o.key}
                onClick={() => setStatusFilter(o.key)}
                testId={`admin-status-${o.key}`}
                label={o.label}
              />
            ))}
          </div>

          {typeFilter === "support" && (
            <select
              value={supportKindFilter}
              onChange={(e) => setSupportKindFilter(e.target.value)}
              data-testid="admin-support-kind"
              className="bg-softblue/40 rounded-full px-4 py-2 text-sm text-ink-800 outline-none cursor-pointer"
            >
              {SUPPORT_REQUEST_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t === "all" ? "All request types" : t.charAt(0).toUpperCase() + t.slice(1)}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Split view */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* List */}
        <div
          className={`lg:col-span-5 ${selected ? "hidden lg:block" : "block"}`}
          data-testid="admin-list-column"
        >
          <div className="bg-white rounded-2xl border border-ink-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-100 flex items-center gap-3 justify-between">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={() =>
                    allVisibleSelected ? onClearSelection() : onSelectAllVisible()
                  }
                  disabled={items.length === 0}
                  aria-label="Select all visible messages"
                  data-testid="inbox-select-all"
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-xs uppercase tracking-[0.15em] text-ink-500 font-semibold">
                  {selectedInboxKeys.size > 0
                    ? `${selectedInboxKeys.size} selected`
                    : `${items.length} ${items.length === 1 ? "result" : "results"}`}
                </span>
              </label>
              {selectedInboxKeys.size > 0 && (
                <div className="flex items-center gap-2" data-testid="inbox-bulk-actions">
                  <button
                    type="button"
                    onClick={onClearSelection}
                    data-testid="inbox-bulk-clear"
                    className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 hover:text-ink-800"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={onAskBulkDelete}
                    data-testid="inbox-bulk-delete"
                    className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-red-700"
                  >
                    <Trash size={12} weight="bold" />
                    Delete Selected
                  </button>
                </div>
              )}
            </div>
            {loading ? (
              <div className="py-16 text-center text-ink-500 text-sm" data-testid="admin-loading">
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-ink-500 text-sm" data-testid="admin-empty">
                No submissions match your filters.
              </div>
            ) : (
              <ul className="divide-y divide-ink-100 max-h-[70vh] overflow-y-auto">
                {items.map((item) => (
                  <ListRow
                    key={`${item._type}-${item.id}`}
                    item={item}
                    active={selectedId === item.id}
                    onSelect={() => setSelectedId(item.id)}
                    selected={selectedInboxKeys.has(inboxKey(item))}
                    onToggle={() => onToggleRow(item)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Detail */}
        <div
          className={`lg:col-span-7 ${selected ? "block" : "hidden lg:block"}`}
          data-testid="admin-detail-column"
        >
          <DetailPanel
            item={selected}
            onStatusChange={onStatusChange}
            onNotesSave={onNotesSave}
            onDelete={onDelete}
            onClose={() => setSelectedId(null)}
          />
        </div>
      </div>
    </div>
  );
};

const TypeChip = ({ active, onClick, icon: Icon, label, count, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
      active
        ? "bg-primary text-white shadow-sm"
        : "bg-white border border-ink-200 text-ink-700 hover:border-primary/40"
    }`}
  >
    {Icon && <Icon size={12} weight="duotone" />}
    {label}
    <span
      className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
        active ? "bg-white/25 text-white" : "bg-ink-100 text-ink-600"
      }`}
    >
      {count}
    </span>
  </button>
);

const StatusChip = ({ active, onClick, label, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
      active ? "bg-white text-primary shadow-sm" : "text-ink-700 hover:text-primary"
    }`}
  >
    {label}
  </button>
);

const ListRow = ({ item, active, onSelect, selected, onToggle }) => {
  const status = inferStatus(item);
  const body = getBody(item);
  return (
    <li>
      <div
        className={`w-full flex items-start gap-2 transition-colors ${
          active ? "bg-softblue/60" : "hover:bg-softblue/30"
        } ${selected ? "bg-red-50/50" : ""}`}
      >
        <label
          className="pl-4 pr-1 py-3.5 cursor-pointer flex items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            aria-label={`Select message from ${item.name || item.email || "unnamed"}`}
            data-testid={`inbox-select-${item._type}-${item.id}`}
            className="h-4 w-4 accent-primary cursor-pointer"
          />
        </label>
        <button
          onClick={onSelect}
          data-testid={`admin-list-row-${item.id}`}
          className="flex-1 text-left pr-4 py-3.5"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <TypeBadge type={item._type} />
                {item._type !== "newsletter" && <StatusPill status={status} />}
              </div>
              <div className="flex items-center gap-2">
                <UserIcon size={12} weight="duotone" className="text-ink-500 shrink-0" />
                <span className="font-semibold text-ink-900 text-sm truncate">
                  {item.name || item.email || "Unnamed"}
                </span>
              </div>
              {item.email && item.name && (
                <div className="text-xs text-ink-500 truncate ml-4">{item.email}</div>
              )}
              {(body || item.subject) && (
                <p className="mt-1 text-xs text-ink-600 line-clamp-2">
                  {item.subject ? <span className="font-semibold">{item.subject}: </span> : null}
                  {body}
                </p>
              )}
            </div>
            <span className="text-[11px] text-ink-500 shrink-0 pt-0.5">{fmtDateShort(item.created_at)}</span>
          </div>
        </button>
      </div>
    </li>
  );
};

// ---------- Chapel Editor (unchanged) ----------
const ChapelEditor = () => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_BASE}/chapel-settings`)
      .then((r) => setForm(r.data))
      .catch(() => toast.error("Couldn't load chapel settings"))
      .finally(() => setLoading(false));
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API_BASE}/admin/chapel-settings`, {
        title: form.title,
        description: form.description,
        schedule: form.schedule,
        zoom_link: form.zoom_link,
        disclaimer: form.disclaimer,
        is_active: form.is_active,
      });
      setForm(res.data);
      toast.success("Chapel settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="py-20 text-center text-ink-500" data-testid="chapel-loading">
        Loading…
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-2xl border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-7 sm:p-9"
      data-testid="admin-chapel-editor"
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <span className="inline-flex items-center gap-2 text-primary">
            <Cross size={18} weight="duotone" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold">Online Chapel</span>
          </span>
          <h2 className="mt-2 font-heading text-2xl text-ink-900 font-medium">Chapel settings</h2>
          <p className="text-sm text-ink-500 mt-1">
            These values appear on the homepage and the Classes page.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.is_active}
            onChange={(e) => setField("is_active", e.target.checked)}
            data-testid="chapel-input-active"
            className="w-4 h-4 accent-primary"
          />
          <span className="text-sm font-semibold text-ink-700">Visible on site</span>
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-semibold text-ink-800 mb-2">Section title</label>
          <input
            type="text"
            value={form.title || ""}
            onChange={(e) => setField("title", e.target.value)}
            data-testid="chapel-input-title"
            className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-ink-800 mb-2">Schedule</label>
          <input
            type="text"
            value={form.schedule || ""}
            onChange={(e) => setField("schedule", e.target.value)}
            placeholder="Leave blank until a specific day/time is confirmed"
            data-testid="chapel-input-schedule"
            className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          />
        </div>
      </div>

      <div className="mt-5">
        <label className="block text-sm font-semibold text-ink-800 mb-2">Zoom link</label>
        <input
          type="url"
          value={form.zoom_link || ""}
          onChange={(e) => setField("zoom_link", e.target.value)}
          placeholder="https://us06web.zoom.us/j/..."
          data-testid="chapel-input-zoom"
          className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
        />
        <p className="mt-1.5 text-xs text-ink-500">
          Leave blank to show &ldquo;Link Coming Soon&rdquo; on the public site.
        </p>
      </div>

      <div className="mt-5">
        <label className="block text-sm font-semibold text-ink-800 mb-2">Description</label>
        <textarea
          rows={4}
          value={form.description || ""}
          onChange={(e) => setField("description", e.target.value)}
          data-testid="chapel-input-description"
          className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-y"
        />
      </div>

      <div className="mt-5">
        <label className="block text-sm font-semibold text-ink-800 mb-2">Disclaimer</label>
        <textarea
          rows={3}
          value={form.disclaimer || ""}
          onChange={(e) => setField("disclaimer", e.target.value)}
          data-testid="chapel-input-disclaimer"
          className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-y"
        />
      </div>

      <div className="mt-7 flex items-center gap-4">
        <button
          onClick={save}
          disabled={saving}
          data-testid="chapel-save-btn"
          className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-6 py-3 font-semibold hover:bg-primary-dark transition-colors disabled:opacity-60"
        >
          <FloppyDisk size={18} weight="duotone" />
          {saving ? "Saving…" : "Save changes"}
        </button>
        <span className="text-xs text-ink-500">
          Last updated {form.updated_at ? new Date(form.updated_at).toLocaleString() : "—"}
        </span>
      </div>
    </div>
  );
};

export default Admin;
